import type { RenderCricket, RenderResource } from "@cc/engine";
import type { Vec2 } from "@cc/protocol";
import type { MatchClient, Vfx } from "./match-client";
import type { Dict } from "./i18n";
import { drawCricketIcon, resolveIcon } from "./icons";
import { RESOURCE_STYLES, slotTheme } from "./theme";
import type { VisionView } from "./vision";
import { inVision, pointInVision } from "./vision";

export interface RenderOpts {
  readonly width: number;
  readonly height: number;
  /** Interpolation factor between the previous and current tick, 0..1. */
  readonly alpha: number;
  /** Strings needed inside the canvas (floating labels). */
  readonly dict: Dict;
  /**
   * When set, the arena is drawn through one cricket's eyes: resources outside
   * its vision are hidden, the opponent vanishes when out of sight, and the
   * rest of the map is fogged. `null`/absent means the omniscient spectator view.
   */
  readonly vision?: VisionView | null;
  /** Which cricket the vision view belongs to, for fog shading. */
  readonly visionSlot?: 0 | 1;
}

const PAD = 18;

export function renderArena(
  ctx: CanvasRenderingContext2D,
  client: MatchClient,
  opts: RenderOpts,
): void {
  const { width, height, alpha, dict } = opts;
  const arenaSize = client.config.arenaSize;
  const vision = opts.vision ?? null;
  const visionSlot = opts.visionSlot;

  ctx.clearRect(0, 0, width, height);

  // Camera: spectator uses the full arena; vision mode follows that cricket so
  // the inset always shows *them* (and their local world) rather than a tiny
  // speck in a corner of the whole map.
  const cam = cameraFor(client, arenaSize, width, height, alpha, vision, visionSlot);
  const { scale, toScreen, offX, offY, size } = cam;

  drawGround(ctx, offX, offY, size, arenaSize, toScreen);
  drawResources(ctx, client, toScreen, scale, client.current, vision);
  drawVision(ctx, client, toScreen, scale, alpha, vision, visionSlot);
  drawCrickets(ctx, client, toScreen, scale, alpha, vision, visionSlot);
  drawVfx(ctx, client, toScreen, scale, vision);
  // Fog sits above the world but below the floating labels, so the visible
  // circle stays crisp while everything the cricket cannot perceive recedes.
  drawFog(ctx, client, toScreen, scale, alpha, vision, visionSlot, width, height);
  drawFloats(ctx, client, toScreen, dict);
}

/**
 * World → screen mapping for the current camera.
 *
 * Vision insets zoom and pan so the viewed cricket sits near the centre with
 * its vision disc filling most of the frame. Without this, a 22-unit disc on a
 * 100-unit map reads as a speck and the cricket itself is easy to miss.
 */
function cameraFor(
  client: MatchClient,
  arenaSize: number,
  width: number,
  height: number,
  alpha: number,
  vision: VisionView | null,
  visionSlot: 0 | 1 | undefined,
): {
  scale: number;
  toScreen: (p: Vec2) => [number, number];
  offX: number;
  offY: number;
  size: number;
} {
  const frame = Math.min(width, height) - PAD * 2;

  let center: Vec2 = [arenaSize / 2, arenaSize / 2];
  let span = arenaSize;

  if (vision && visionSlot !== undefined) {
    const cricket =
      client.current.crickets.find((c) => c.slot === visionSlot) ??
      client.current.crickets[visionSlot];
    if (cricket) {
      const prev = client.previous.crickets.find((p) => p.id === cricket.id);
      center = lerp(prev ? prev.position : cricket.position, cricket.position, alpha);
      // Vision disc + a little margin so the rim and nearby loot stay readable.
      span = Math.max(vision.visionRadius * 2.35, 28);
    }
  }

  const scale = frame / span;
  const toScreen = (p: Vec2): [number, number] => [
    width / 2 + (p[0] - center[0]) * scale,
    height / 2 + (p[1] - center[1]) * scale,
  ];

  // Arena rectangle in screen space (may extend past the canvas when zoomed).
  const [ax, ay] = toScreen([0, 0]);
  const size = arenaSize * scale;
  return { scale, toScreen, offX: ax, offY: ay, size };
}

/* ------------------------------------------------------------------ */
/* Background                                                          */
/* ------------------------------------------------------------------ */

function drawGround(
  ctx: CanvasRenderingContext2D,
  offX: number,
  offY: number,
  size: number,
  arenaSize: number,
  toScreen: (p: Vec2) => [number, number],
): void {
  const grad = ctx.createRadialGradient(
    offX + size / 2,
    offY + size / 2,
    size * 0.05,
    offX + size / 2,
    offY + size / 2,
    size * 0.75,
  );
  grad.addColorStop(0, "#0b1120");
  grad.addColorStop(1, "#050710");
  ctx.fillStyle = grad;
  ctx.fillRect(offX, offY, size, size);

  // Grid in world units so cell size stays honest under camera zoom.
  const cells = 10;
  const step = arenaSize / cells;
  ctx.strokeStyle = "#1a2438";
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= cells; i++) {
    const t = i * step;
    const [vx0, vy0] = toScreen([t, 0]);
    const [, vy1] = toScreen([t, arenaSize]);
    ctx.moveTo(vx0, vy0);
    ctx.lineTo(vx0, vy1);
    const [hx0, hy] = toScreen([0, t]);
    const [hx1] = toScreen([arenaSize, t]);
    ctx.moveTo(hx0, hy);
    ctx.lineTo(hx1, hy);
  }
  ctx.stroke();

  // Corner brackets give the arena a "containment field" feel.
  const b = Math.max(14, size * 0.05);
  ctx.strokeStyle = "#2b3a5c";
  ctx.lineWidth = 2;
  ctx.beginPath();
  const corners: [number, number, number, number][] = [
    [offX, offY, 1, 1],
    [offX + size, offY, -1, 1],
    [offX, offY + size, 1, -1],
    [offX + size, offY + size, -1, -1],
  ];
  for (const [x, y, dx, dy] of corners) {
    ctx.moveTo(x + dx * b, y);
    ctx.lineTo(x, y);
    ctx.lineTo(x, y + dy * b);
  }
  ctx.stroke();

  ctx.strokeStyle = "#1d2740";
  ctx.lineWidth = 1;
  ctx.strokeRect(offX + 0.5, offY + 0.5, size - 1, size - 1);
}

/* ------------------------------------------------------------------ */
/* Resources                                                           */
/* ------------------------------------------------------------------ */

function drawResources(
  ctx: CanvasRenderingContext2D,
  client: MatchClient,
  toScreen: (p: Vec2) => [number, number],
  scale: number,
  state: MatchClient["current"],
  vision: VisionView | null,
): void {
  const tick = client.tick;
  for (const r of state.resources) {
    // In vision mode, anything this cricket cannot perceive is simply not drawn.
    // That is the whole point: the viewer should experience the same blind spot,
    // not be told about it.
    if (!inVision(vision, r.id)) continue;
    const [x, y] = toScreen(r.position);
    // Re-mask a trap the cricket has not identified. `state.resources` already
    // reports identified traps as traps, but only globally — in vision mode we
    // take the per-cricket view instead, so a trap one cricket scouted still
    // looks like bait to the other.
    const kind = vision ? (vision.visible.get(r.id)?.kind ?? r.kind) : r.kind;
    const style = RESOURCE_STYLES[kind];
    const pulse = 0.5 + 0.5 * Math.sin(tick / 6 + hashId(r.id));

    // Soft halo.
    const radius = Math.max(9, scale * 2.6);
    const halo = ctx.createRadialGradient(x, y, 0, x, y, radius);
    halo.addColorStop(0, hexA(style.color, 0.34 + pulse * 0.2));
    halo.addColorStop(1, hexA(style.color, 0));
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();

    // Rotating diamond for crystals, static glyph otherwise.
    ctx.save();
    ctx.translate(x, y);
    ctx.shadowColor = style.color;
    ctx.shadowBlur = 10 + pulse * 8;
    ctx.fillStyle = style.color;

    if (kind === "crystal" || kind === "trap") {
      ctx.rotate(((tick / 40) % (Math.PI * 2)) * (kind === "trap" ? 1 : 0.4));
      const s = Math.max(5, scale * 1.7);
      ctx.beginPath();
      ctx.moveTo(0, -s);
      ctx.lineTo(s * 0.62, 0);
      ctx.lineTo(0, s);
      ctx.lineTo(-s * 0.62, 0);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.font = `${Math.max(11, scale * 2.4)}px ui-monospace, monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(style.glyph, 0, 0);
    }
    ctx.restore();

    // Value chip, only when it is readable.
    if (scale > 4) {
      ctx.fillStyle = hexA(style.color, 0.85);
      ctx.font = "9px ui-monospace, monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(String(r.value), x, y + Math.max(8, scale * 2.6));
    }
  }
}

/* ------------------------------------------------------------------ */
/* Vision cones                                                        */
/* ------------------------------------------------------------------ */

/**
 * Faint vision discs.
 *
 * These are drawn very subtly on purpose. They exist to make the information
 * asymmetry *legible* — a viewer should be able to see that a cricket genuinely
 * cannot see a trap three units outside its own radius — without turning the
 * arena into a venn-diagram.
 */
function drawVision(
  ctx: CanvasRenderingContext2D,
  client: MatchClient,
  toScreen: (p: Vec2) => [number, number],
  scale: number,
  alpha: number,
  vision: VisionView | null,
  visionSlot: 0 | 1 | undefined,
): void {
  const radius = client.config.stats.visionRadius * scale;
  for (const c of client.current.crickets) {
    if (!c.alive) continue;
    // In vision mode only the viewed cricket's disc is drawn; the other cricket's
    // would reveal nothing but clutter.
    if (vision && c.slot !== visionSlot) continue;
    const prev = client.previous.crickets.find((p) => p.id === c.id);
    const p = lerp(prev ? prev.position : c.position, c.position, alpha);
    const [x, y] = toScreen(p);
    const theme = slotTheme(c.slot);

    const g = ctx.createRadialGradient(x, y, radius * 0.15, x, y, radius);
    g.addColorStop(0, hexA(theme.color, 0.055));
    g.addColorStop(1, hexA(theme.color, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Fog of war for vision mode.
 *
 * Drawn as a huge ring whose hole is the viewed cricket's vision disc, which is
 * cheaper and cleaner than compositing a per-pixel mask: one path covering the
 * whole box with a circular counter-clockwise hole punches the spotlight, and a
 * radial gradient feathers the edge so the boundary reads as "fading knowledge"
 * rather than a hard circle.
 */
function drawFog(
  ctx: CanvasRenderingContext2D,
  client: MatchClient,
  toScreen: (p: Vec2) => [number, number],
  scale: number,
  alpha: number,
  vision: VisionView | null,
  visionSlot: 0 | 1 | undefined,
  width: number,
  height: number,
): void {
  if (!vision || visionSlot === undefined) return;
  const cricket =
    client.current.crickets.find((c) => c.slot === visionSlot) ??
    client.current.crickets[visionSlot];
  if (!cricket) return;
  const prev = client.previous.crickets.find((p) => p.id === cricket.id);
  const p = lerp(prev ? prev.position : cricket.position, cricket.position, alpha);
  const [x, y] = toScreen(p);
  const radius = vision.visionRadius * scale;

  ctx.save();
  const g = ctx.createRadialGradient(x, y, radius * 0.75, x, y, radius * 1.9);
  g.addColorStop(0, "rgba(4,6,12,0)");
  g.addColorStop(1, "rgba(4,6,12,0.9)");
  ctx.fillStyle = g;

  // One rect for the whole frame, plus the vision disc traversed the other way
  // so `evenodd` punches it out. Cheaper and cleaner than a pixel mask.
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  ctx.arc(x, y, radius, 0, Math.PI * 2, true);
  ctx.fill("evenodd");
  ctx.restore();

  // A crisp rim so the exact edge of this cricket's world is unambiguous.
  ctx.strokeStyle = "rgba(255,255,255,0.16)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();
}

/* ------------------------------------------------------------------ */
/* Crickets                                                            */
/* ------------------------------------------------------------------ */

function drawCrickets(
  ctx: CanvasRenderingContext2D,
  client: MatchClient,
  toScreen: (p: Vec2) => [number, number],
  scale: number,
  alpha: number,
  vision: VisionView | null,
  visionSlot: 0 | 1 | undefined,
): void {
  // Draw the followed cricket last so it sits on top of overlapping sprites.
  const ordered = [...client.current.crickets].sort((a, b) => {
    if (visionSlot === undefined) return a.slot - b.slot;
    if (a.slot === visionSlot) return 1;
    if (b.slot === visionSlot) return -1;
    return a.slot - b.slot;
  });

  for (const c of ordered) {
    const prev = client.previous.crickets.find((p) => p.id === c.id);
    const p = lerp(prev ? prev.position : c.position, c.position, alpha);

    // In vision mode the viewed cricket is "you" and renders normally, but an
    // opponent outside the vision radius is not drawn at all. A ghosted enemy
    // would defeat the whole exercise: the viewer is supposed to be just as
    // blind as the cricket.
    if (vision && visionSlot !== undefined && c.slot !== visionSlot && !pointInVision(vision, p)) {
      continue;
    }

    const [x, y] = toScreen(p);
    const theme = slotTheme(c.slot);
    // Vision insets zoom in; keep a floor so the followed cricket never reads as a speck.
    const base = Math.max(vision ? 12 : 9, scale * 2.2);

    if (!c.alive) {
      // A dead cricket is a dim, hollow ring: present but inert.
      ctx.strokeStyle = hexA(theme.dim, 0.5);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, y, base * 0.8, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = "rgba(255,255,255,0.22)";
      ctx.font = `${base}px ui-monospace, monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("✕", x, y);
      continue;
    }

    // Outer glow.
    const glowR = base * 2.1;
    const g = ctx.createRadialGradient(x, y, 0, x, y, glowR);
    g.addColorStop(0, hexA(theme.color, 0.5));
    g.addColorStop(1, hexA(theme.color, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, glowR, 0, Math.PI * 2);
    ctx.fill();

    // Defend shield.
    if (c.defending) {
      ctx.strokeStyle = hexA("#fbbf24", 0.85);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, base + 6, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Spinning-blade aura.
    if (c.bladeActive) {
      const spin = (client.tick / 4) % (Math.PI * 2);
      ctx.strokeStyle = hexA("#e879f9", 0.9);
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(x, y, base + 10, spin, spin + Math.PI * 1.4);
      ctx.stroke();
      ctx.strokeStyle = hexA("#e879f9", 0.35);
      ctx.beginPath();
      ctx.arc(x, y, base + 10, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Flee dash ring.
    if (c.fleeing) {
      ctx.strokeStyle = hexA("#38bdf8", 0.7);
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, y, base + 8, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Collect channel: an arc that fills as the channel progresses.
    if (c.collecting) {
      ctx.strokeStyle = hexA("#22d3ee", 0.9);
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(x, y, base + 3, -Math.PI / 2, -Math.PI / 2 + Math.PI * 1.4);
      ctx.stroke();
    }

    // Body.
    ctx.save();
    ctx.shadowColor = theme.color;
    ctx.shadowBlur = 16;
    ctx.fillStyle = theme.color;
    ctx.beginPath();
    ctx.arc(x, y, base, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    const info = client.infos[c.slot];
    const icon = resolveIcon(info?.icon, c.slot, c.id);
    drawCricketIcon(ctx, icon, x, y, base, theme.color, theme.label);

    // Ring name above the ball — the edited AI name (or bot persona) must be
    // readable in the arena itself, not only in the HUD.
    const ringName = client.displayName(c.id);
    if (ringName && (scale > 2.2 || vision)) {
      const short = ringName.length > 12 ? `${ringName.slice(0, 11)}…` : ringName;
      ctx.font = `bold ${Math.max(9, Math.min(12, base * 0.85))}px ui-monospace, monospace`;
      ctx.textBaseline = "bottom";
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillText(short, x + 1, y - base - 11);
      ctx.fillStyle = hexA(theme.color, 0.98);
      ctx.fillText(short, x, y - base - 12);
    }

    // HP arc around the body.
    const hpFrac = Math.max(0, c.hp / c.maxHp);
    ctx.strokeStyle = hpFrac > 0.5 ? hexA("#34d399", 0.9) : hpFrac > 0.25 ? hexA("#fbbf24", 0.95) : hexA("#ef4444", 1);
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x, y, base + 9, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * hpFrac);
    ctx.stroke();

    // Action label, so a viewer can see intent without the side panel.
    const label = c.action === "WAIT" ? "" : c.action;
    if (label && (scale > 2.5 || vision)) {
      ctx.fillStyle = hexA(theme.color, 0.95);
      ctx.font = "bold 9px ui-monospace, monospace";
      ctx.textBaseline = "bottom";
      ctx.fillText(label, x, y - base - 26);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Effects                                                             */
/* ------------------------------------------------------------------ */

function drawVfx(
  ctx: CanvasRenderingContext2D,
  client: MatchClient,
  toScreen: (p: Vec2) => [number, number],
  scale: number,
  vision: VisionView | null,
): void {
  const tick = client.tick;

  for (const v of client.vfx) {
    const age = tick - v.bornTick;
    if (age < 0 || age > v.lifeTicks) continue;
    // Effects outside the viewed cricket's vision are not shown either, or the
    // fog would leak the information it exists to withhold (a trap blast you
    // cannot see is information).
    if (!pointInVision(vision, v.position)) continue;
    const t = age / v.lifeTicks;
    const fade = 1 - t;
    const [x, y] = toScreen(v.position);

    switch (v.kind) {
      case "hit": {
        // Expanding ring + radial sparks.
        ctx.strokeStyle = hexA("#ff2d55", fade * 0.9);
        ctx.lineWidth = 2.5 * fade + 0.5;
        const r = scale * (2 + t * 5);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = hexA("#ffd166", fade * 0.85);
        ctx.lineWidth = 1.5;
        for (let i = 0; i < 5; i++) {
          const a = (Math.PI * 2 * i) / 5 + t;
          const inner = r * 0.5;
          const outer = r * (1.1 + t * 0.7);
          ctx.beginPath();
          ctx.moveTo(x + Math.cos(a) * inner, y + Math.sin(a) * inner);
          ctx.lineTo(x + Math.cos(a) * outer, y + Math.sin(a) * outer);
          ctx.stroke();
        }
        break;
      }
      case "trap": {
        // A big, unmissable blast: the "OOPS" moment deserves the loudest effect.
        const r = scale * (3 + t * 20);
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, hexA("#fff3c4", fade * 0.9));
        g.addColorStop(0.35, hexA("#f97316", fade * 0.75));
        g.addColorStop(1, hexA("#ef4444", 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = hexA("#ef4444", fade);
        ctx.lineWidth = 4 * fade + 1;
        ctx.beginPath();
        ctx.arc(x, y, scale * (2 + t * 14), 0, Math.PI * 2);
        ctx.stroke();

        ctx.fillStyle = hexA("#fff", fade * 0.95);
        ctx.font = `bold ${Math.max(13, scale * 3)}px ui-monospace, monospace`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("💀", x, y - t * scale * 8);
        break;
      }
      case "collect": {
        ctx.strokeStyle = hexA("#22d3ee", fade);
        ctx.lineWidth = 2;
        const r = scale * (1.5 + t * 4);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.stroke();
        // Rising sparkles.
        ctx.fillStyle = hexA("#67e8f9", fade);
        for (let i = 0; i < 4; i++) {
          const a = (Math.PI * 2 * i) / 4 + t * 2;
          ctx.beginPath();
          ctx.arc(x + Math.cos(a) * r * 0.7, y + Math.sin(a) * r * 0.7 - t * 12, 2.2 * fade, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
      case "spawn": {
        const r = scale * (0.5 + t * 3.2);
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, hexA("#93c5fd", fade * 0.5));
        g.addColorStop(1, hexA("#38bdf8", 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "scout": {
        ctx.strokeStyle = hexA("#22d3ee", fade * 0.7);
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.arc(x, y, scale * (2 + t * 10), 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        break;
      }
      case "miss": {
        ctx.strokeStyle = hexA("#94a3b8", fade * 0.8);
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x - 5, y - 5);
        ctx.lineTo(x + 5, y + 5);
        ctx.moveTo(x + 5, y - 5);
        ctx.lineTo(x - 5, y + 5);
        ctx.stroke();
        break;
      }
      case "kill": {
        const r = scale * (2 + t * 12);
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, hexA("#ffffff", fade * 0.8));
        g.addColorStop(0.4, hexA("#ef4444", fade * 0.6));
        g.addColorStop(1, hexA("#7f1d1d", 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "bomb": {
        const r = scale * (4 + t * 18);
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, hexA("#fff7ed", fade * 0.95));
        g.addColorStop(0.3, hexA("#f97316", fade * 0.8));
        g.addColorStop(1, hexA("#ea580c", 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = hexA("#fb923c", fade);
        ctx.lineWidth = 3 * fade + 1;
        ctx.beginPath();
        ctx.arc(x, y, scale * (3 + t * 12), 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case "blade": {
        ctx.strokeStyle = hexA("#e879f9", fade * 0.9);
        ctx.lineWidth = 2.5;
        const spin = t * Math.PI * 4;
        ctx.beginPath();
        ctx.arc(x, y, scale * (3 + t * 6), spin, spin + Math.PI * 1.5);
        ctx.stroke();
        break;
      }
    }
  }
}

function drawFloats(
  ctx: CanvasRenderingContext2D,
  client: MatchClient,
  toScreen: (p: Vec2) => [number, number],
  d: Dict,
): void {
  const tick = client.tick;
  for (const f of client.floats) {
    const age = tick - f.bornTick;
    if (age < 0 || age > f.lifeTicks) continue;
    const t = age / f.lifeTicks;
    const [x, y] = toScreen(f.position);
    // Labelled floats carry a translation key; numeric ones are language-neutral.
    const label =
      f.key === "channelLost"
        ? d.channelLost
        : f.key === "collectMissed"
          ? d.collectMissedFloat
          : f.text;
    ctx.globalAlpha = 1 - t * t;
    ctx.fillStyle = f.color;
    ctx.font =
      'bold 13px ui-monospace, "PingFang SC", "Microsoft YaHei", "Noto Sans SC", monospace';
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.shadowColor = "#000";
    ctx.shadowBlur = 4;
    ctx.fillText(label, x, y - 16 - t * 26);
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  }
}

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */

function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function hexA(hex: string, alpha: number): string {
  const clamped = Math.max(0, Math.min(1, alpha));
  const v = Math.round(clamped * 255)
    .toString(16)
    .padStart(2, "0");
  return `${hex}${v}`;
}

function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 997;
  return h / 997;
}

export { PAD };
